import { Link } from "react-router-dom";
import { BarChart3, TrendingUp, ArrowRight, Globe, Calculator } from "lucide-react";

const services = [
  "Carbon Emission Calculation",
  "Carbon Inventory & Reporting",
  "CCTS Compliance Advisory",
  "Transition from PAT Scheme",
  "Carbon Credit Generation",
  "Carbon Credit Trading Support",
  "CBAM (EU) Advisory",
];

export default function CarbonCCTSPage() {
  return (
    <div className="pt-20" data-testid="carbon-ccts-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-teal-950 to-slate-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm font-medium mb-6">
              <Globe className="h-4 w-4" /> India + Global Carbon Markets
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight">
              Carbon Market & CCTS
            </h1>
            <p className="mt-6 text-xl text-slate-400 leading-relaxed">
              Navigate India's Carbon Credit Trading Scheme and global carbon markets. Turn your compliance cost into a revenue stream.
            </p>
            <div className="mt-8 p-6 rounded-xl bg-teal-500/10 border border-teal-500/20">
              <p className="text-teal-300 font-semibold text-lg italic">
                "Compliance is a cost. Carbon strategy turns it into revenue."
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Carbon Services</h2>
          <p className="text-lg text-slate-600 mb-12 max-w-3xl">End-to-end carbon management — from measurement to monetization.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((svc, i) => (
              <div key={i} className="p-6 rounded-xl border border-slate-200 hover:border-teal-300 hover:shadow-lg transition-all group">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center mb-4 group-hover:bg-teal-500 group-hover:text-white transition-all">
                  <BarChart3 className="h-5 w-5 text-teal-600 group-hover:text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{svc}</h3>
                <p className="text-slate-600 mt-2 text-sm">Professional advisory and implementation support.</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Carbon Liability Checker */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-6">
              <Calculator className="h-8 w-8 text-teal-600" />
            </div>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Check Your Carbon Liability</h2>
            <p className="text-lg text-slate-600 mb-8">
              Use our emission calculator to understand your carbon footprint and potential CCTS obligations.
            </p>
            <Link
              to="/emission-calculator"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-teal-500 to-emerald-500 text-white font-bold rounded-xl text-lg hover:opacity-90 transition-all shadow-lg shadow-teal-500/20"
              data-testid="carbon-liability-cta"
            >
              <Calculator className="h-5 w-5" />
              Open Carbon Calculator
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CCTS Explainer */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">What is CCTS?</h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>The <strong>Carbon Credit Trading Scheme (CCTS)</strong> is India's market mechanism under the Energy Conservation Amendment Act, 2022. It mandates obligated entities to reduce emissions or purchase carbon credits.</p>
                <p>The scheme transitions from the earlier PAT (Perform, Achieve & Trade) system and aligns India with global carbon pricing mechanisms.</p>
                <p>With EU's CBAM coming into effect, Indian exporters also need to account for embedded carbon in their products.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="p-5 rounded-xl bg-teal-50 border border-teal-100">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="h-5 w-5 text-teal-600" />
                  <h3 className="font-bold text-slate-900">Carbon Credit Generation</h3>
                </div>
                <p className="text-slate-600 text-sm">Identify and develop projects that generate verified carbon credits for trading.</p>
              </div>
              <div className="p-5 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center gap-3 mb-2">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <h3 className="font-bold text-slate-900">CBAM Advisory</h3>
                </div>
                <p className="text-slate-600 text-sm">Navigate EU's Carbon Border Adjustment Mechanism for exports.</p>
              </div>
              <div className="p-5 rounded-xl bg-emerald-50 border border-emerald-100">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="h-5 w-5 text-emerald-600" />
                  <h3 className="font-bold text-slate-900">PAT to CCTS Transition</h3>
                </div>
                <p className="text-slate-600 text-sm">Seamless migration from PAT scheme to the new carbon credit framework.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-teal-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Turn Carbon into Revenue?</h2>
          <p className="text-teal-100 text-lg mb-8">Let us assess your carbon position and build a monetization strategy.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="px-8 py-3 bg-white text-teal-700 font-bold rounded-lg hover:bg-teal-50 transition-all flex items-center gap-2">
              Talk to Carbon Expert <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/emission-calculator" className="px-8 py-3 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all">
              Calculate Emissions
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
